import React, { createContext, useRef, useState } from 'react';
import { downloadAPI } from 'api/api';
import LoadingPlaceholder from 'components/common/loading-placeholder/LoadingPlaceholder';
import { Box, Stack } from '@mui/material';
import useBundleTranslation from 'i18n';
import toast from 'app/toast';
import { AxiosError } from 'axios';

export interface DownloadManagerHandlerProps {
    elementId: number;
    segmentId: number;
    format: string;
}
export const DownloadManagerContext = createContext(function (props: DownloadManagerHandlerProps, options?: any) {});
const CheckInterval = 10000;
export default function DownloadManager({ children }: { children: any }) {
    // Waiting for file to generate, show loading placeholder and check status every CheckInterval seconds until file is ready.
    const [isActive, setIsActive] = useState<Boolean>(false);
    // File is ready and downloading started, show loading placeholder until file is fully downloaded.
    const [isDownloading, setIsDownloading] = useState<Boolean>(false);
    const [hashPref, setHashPref] = useState<string>('');

    const isActiveRef = useRef(isActive);
    isActiveRef.current = isActive;

    const hashPrefRef = useRef(hashPref);
    hashPrefRef.current = hashPref;
    const { t } = useBundleTranslation();

    const onDownloadFailed = (message: string) => {
        toast('error', t('download_failed'), message, { duration: 5000 });
        cancelDownload();
    };

    const runDownload = (props: DownloadManagerHandlerProps, options?: any) => {
        const checker = () => {
            setTimeout(() => {
                let data = options ?? {};
                if (hashPrefRef.current) {
                    // @ts-ignore
                    data.download_pref = hashPrefRef.current;
                }
                downloadAPI.element.checkDownload(props, data).then(async (response) => {
                    if (isActiveRef.current) {
                        setHashPref(response.data.data.pref);
                        if (response.data.status == 'ERROR') {
                            onDownloadFailed(response.data?.message ?? t('error'));
                        } else {
                            if (response.data.data.ready) {
                                setIsDownloading(true);
                                setIsActive(false);
                                await downloadAPI.element.getDownload(props, data);
                                setIsDownloading(false);
                            } else {
                                checker();
                            }
                        }
                    }
                });
            }, CheckInterval);
        };

        setIsActive(true);
        downloadAPI.element
            .runDownload(props, options)
            .then((response) => {
                if (response.data.status == 'ERROR') {
                    onDownloadFailed(response.data?.message ?? '');
                }
            })
            .catch((error: AxiosError) => {
                // Ignore 504 status (Gateway Timeout),
                // The request to start generation take a long time, and the connection may be terminated due to a timeout.
                // The result will be received via Check Requests when the file is ready.
                const status = error.response?.status;
                if (status && status != 504) {
                    onDownloadFailed(error.message);
                }
            });
        setTimeout(() => checker(), 1000);
    };

    const cancelDownload = () => {
        setIsActive(false);
    };

    return (
        <DownloadManagerContext.Provider value={runDownload}>
            {(isActive || isDownloading) && (
                <LoadingPlaceholder
                    sx={{
                        zIndex: (theme) => theme.zIndex.modal + 1,
                    }}
                >
                    <Stack direction="column" alignItems="center">
                        <Box>{t('downloading_long')}</Box>
                        <Box onClick={() => cancelDownload()} sx={{ cursor: 'pointer' }}>
                            {t('cancel')}
                        </Box>
                    </Stack>
                </LoadingPlaceholder>
            )}
            {children}
        </DownloadManagerContext.Provider>
    );
}
